import {ReplaySubject, combineLatest} from 'rxjs';
import {filter, map, withLatestFrom} from 'rxjs/operators';
import {div, gt, minus, numUtil, times} from '@/helper/num';
import {calcValueBalanceUtils} from '@/helper/calc-value-balance';
import {calcMoneyData$} from '@/calc/fiat';
import {inAssetCurrencies$} from '@/config';
import {indexPricesCache$} from '@/price-index/price-index';
import {currenciesCache$} from '@/config';
import {isUtaSpot$} from '@/user/user';
import {innerCurrencies$, marginAccountsCache$} from './accounts-cache';
import type {
  CalcMoneyData,
  CurrencyEntity,
  FundAccount,
  FundAccountsData,
  MarginCurrencyEntity,
  PriceEntity,
  ws,
} from '@/types';

type MarginAccountRaw = ws.MarginAccountRaw;

// 包含inAssetsDisplay 为1和0的资产
const allFundAccountsData$ = new ReplaySubject<FundAccount[]>(1);
const allFundScaleAccountsData$ = new ReplaySubject<FundAccount[]>(1);

// 有效钱包 inAssetsDisplay为1
export const fundAccountsData$ = new ReplaySubject<FundAccountsData>(1);
export const fundScaleAccountsData$ = new ReplaySubject<FundAccountsData>(1);

// 无效钱包 inAssetsDisplay为0
export const fundInvalidAccountsData$ = new ReplaySubject<FundAccountsData>(1);
export const fundInvalidScaleAccountsData$ = new ReplaySubject<FundAccountsData>(1);

combineLatest([isUtaSpot$, marginAccountsCache$, innerCurrencies$, indexPricesCache$, calcMoneyData$, currenciesCache$])
  .pipe(
    filter(([isUtaSpot]) => isUtaSpot),
    map(convertAccounts),
    withLatestFrom(inAssetCurrencies$)
  )
  .subscribe(([[accounts, accountsScale], inAssetCurrencies]) => {
    allFundAccountsData$.next(accounts);
    allFundScaleAccountsData$.next(accountsScale);

    updateAccount(inAssetCurrencies, accounts, accountsScale);
  });

inAssetCurrencies$
  .pipe(withLatestFrom(allFundAccountsData$, allFundScaleAccountsData$))
  .subscribe(([inAssetCurrencies, accounts, accountsScale]) => {
    updateAccount(inAssetCurrencies, accounts, accountsScale);
  });

function updateAccount(inAssetCurrencies, accounts, accountsScale) {
  fundAccountsData$.next({
    accounts: accounts.filter(({currency}) => inAssetCurrencies.includes(currency)),
  });
  fundInvalidAccountsData$.next({
    accounts: accounts.filter(({currency}) => !inAssetCurrencies.includes(currency)),
  });

  fundScaleAccountsData$.next({
    accounts: accountsScale.filter(({currency}) => inAssetCurrencies.includes(currency)),
  });
  fundInvalidScaleAccountsData$.next({
    accounts: accountsScale.filter(({currency}) => !inAssetCurrencies.includes(currency)),
  });
}

function convertAccounts([isUtaSpot, cache, currencies, indexPricesCache, calcMoneyData, currenciesCache$]: [
  boolean,
  Map<string, MarginAccountRaw>,
  MarginCurrencyEntity[],
  Map<string, PriceEntity>,
  CalcMoneyData,
  Map<string, CurrencyEntity>
]): [FundAccount[], FundAccount[]] {
  if (!isUtaSpot) {
    return [[], []];
  }
  const innerAccounts = cache
    .toArray((account: MarginAccountRaw) =>
      convertAccount(account, currencies, indexPricesCache, calcMoneyData, currenciesCache$)
    )
    .filter(Boolean)
    .sort((a, b) => (gt(b.totalBalanceValue, a.totalBalanceValue) ? 1 : -1));
  return convertOriginAccounts(innerAccounts, calcMoneyData);
}

interface InnerAccount {
  time: number;
  currency: string;
  currencyCode: number;
  displayCurrency: string;
  name: string;
  fundEv: string;
  lockedWithdrawEv: string;
  totalBalanceMoneyWU: string;
  totalBalanceValue: string;
  valuePrecision: number;
  valueScale: number;
  totalBalanceUsdt: string;
  totalBalanceBtc: string;
}

function convertAccount(
  {lastUpdateTimeNs, fundEv, currency, lockedWithdrawEv}: MarginAccountRaw,
  currencies: MarginCurrencyEntity[],
  indexPricesCache: Map<string, PriceEntity>,
  calcMoneyData: CalcMoneyData,
  currenciesCache: Map<string, CurrencyEntity>
): InnerAccount {
  const currencyInfo = currencies.find((o) => o.currency === currency);

  const usdCurrency = currenciesCache.get('USD');
  const btcCurrency = currenciesCache.get('BTC');

  const indexPrice = indexPricesCache.get('.' + currency);
  const usdtRate = indexPricesCache.get('.USDT');
  const btcRate = indexPricesCache.get('.BTC');

  if (!currencyInfo || !indexPrice || !btcCurrency || !usdCurrency || !usdtRate || !btcRate) {
    return null;
  }

  const {valuePrecision, code, name, displayCurrency} = currencyInfo;
  const {valueScale} = currenciesCache.get(currency);
  const [totalBalanceMoneyWU, totalBalanceValue] = calcMoneyData(displayCurrency, fundEv, true);

  const {valueFactor: usdValueFactor, valuePrecision: usdValuePrecision} = usdCurrency;
  const {valuePrecision: btcValuePrecision} = btcCurrency;

  const totalBalanceUsdEv = convertBalanceToUsdEv(fundEv, currency, indexPricesCache);
  const totalBalanceUsdtEv = div(times(totalBalanceUsdEv, usdValueFactor), usdtRate.priceEp);
  const totalBalanceBtcEv = div(times(totalBalanceUsdEv, usdValueFactor), btcRate.priceEp);

  return {
    time: Math.floor(lastUpdateTimeNs / 1e6),
    currency,
    currencyCode: code,
    displayCurrency,
    name,
    fundEv,
    lockedWithdrawEv,
    totalBalanceMoneyWU,
    totalBalanceValue,
    valuePrecision,
    valueScale,
    totalBalanceUsdt: numUtil.delimit(numUtil.floor(totalBalanceUsdtEv, usdValuePrecision)),
    totalBalanceBtc: numUtil.delimit(numUtil.floor(totalBalanceBtcEv, btcValuePrecision)),
  };
}

function convertOriginAccounts(
  innerAccounts: InnerAccount[],
  calcMoneyData: CalcMoneyData
): [FundAccount[], FundAccount[]] {
  const originAccounts = [];
  const accountsScale = [];

  innerAccounts.forEach((account) => {
    const {
      time,
      currency,
      currencyCode,
      displayCurrency,
      name,
      fundEv,
      lockedWithdrawEv,
      totalBalanceMoneyWU,
      totalBalanceValue,
      valuePrecision,
      valueScale,
      totalBalanceUsdt,
      totalBalanceBtc,
    } = account;
    const freezeBalanceEv = lockedWithdrawEv;
    const availableBalanceEv = minus(fundEv, freezeBalanceEv);
    const availableBalance = calcBalance(availableBalanceEv, valuePrecision);
    const availableBalance2 = calcBalance(availableBalanceEv, 2);
    const availableBalance4 = calcBalance(availableBalanceEv, 4);
    const [availableBalanceMoneyWU] = calcMoneyData(displayCurrency, availableBalanceEv, true);

    const baseData = {
      time,
      currency,
      currencyCode,
      displayCurrency,
      name,
      totalBalance: calcBalance(fundEv, valuePrecision),
      totalBalanceMoneyWU,
      totalBalanceValue,
      availableBalance,
      availableBalanceWU: availableBalance + ' ' + currency,
      availableBalance2,
      availableBalance2WU: availableBalance2 + ' ' + currency,
      availableBalance4,
      availableBalance4WU: availableBalance4 + ' ' + currency,
      availableBalanceMoneyWU,
      lockedWithdraw: calcBalance(lockedWithdrawEv, valuePrecision),
      freezeBalance: calcBalance(freezeBalanceEv, valuePrecision),
      totalBalanceUsdt,
      totalBalanceBtc,
    };
    accountsScale.push({
      ...baseData,
      totalBalanceEv: convertScale(fundEv, valueScale),
      availableBalanceEv: convertScale(availableBalanceEv, valueScale),
      lockedWithdrawEv: convertScale(lockedWithdrawEv, valueScale),
      freezeBalanceEv: convertScale(freezeBalanceEv, valueScale),
    });

    originAccounts.push({...baseData, totalBalanceEv: fundEv, availableBalanceEv, lockedWithdrawEv, freezeBalanceEv});
  });

  return [originAccounts, accountsScale];
}

function calcBalance(balanceEv, valuePrecision): string {
  return numUtil.delimit(numUtil.floor(balanceEv, valuePrecision, 0));
}

function convertScale(balanceEv, scale): string {
  return numUtil.floor(balanceEv, 0, -scale);
}

function convertBalanceToUsdEv(balanceEv, currency, indexPricesCache) {
  const currentIndexPrice = indexPricesCache.get('.' + currency);
  if (!currentIndexPrice) {
    return 0;
  }

  const balanceFactor = 1;
  const valueFactor = 1;
  return calcValueBalanceUtils.getValueEv(
    balanceEv,
    currentIndexPrice.priceEp,
    balanceFactor,
    currentIndexPrice.priceFactor,
    valueFactor
  );
}
